import { css } from "@emotion/react";
import { ReactElement } from "react";
import { Field, FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Close, Image, Info } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, Text, Tooltip } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { isInIframe } from "helpers/app";
import { selectDefaultLogoUrl } from "store/entities/application-settings/selectors";
import { withVar } from "themes/helpers";

import { LogoSwitch } from "./LogoSwitch";
import { LogoUploadField } from "./LogoUploadField";

const getLogoName = (url: string, isLogoDefault: boolean): string => {
  if (!url) {
    return "No logo";
  }
  if (isLogoDefault) {
    const isEmbeddedInIframe = isInIframe();

    return isEmbeddedInIframe ? "Default Text logo" : "Default HelpDesk logo";
  }
  return url.split("/").pop() || "";
};

const logoFileNameStyles = css`
  text-overflow: ellipsis;
  max-width: 25ch;
  overflow: hidden;
  white-space: nowrap;
`;

const fileRecommendationStyles = css`
  color: ${withVar(DesignToken.ContentBasicSecondary)};

  & > div {
    display: inline;
  }
  & ul {
    display: block;
    list-style: disc outside none;
    padding: 0 0 0 20px;
  }
`;

interface ILogoSectionProps {
  logoUrl: string;
  templateID: string;
  showLogo: boolean;
  setTransactionID: (transactionId: string) => void;
}

export function LogoSection({ logoUrl, templateID, showLogo, setTransactionID }: ILogoSectionProps): ReactElement {
  const defaultLogoUrl = useSelector(selectDefaultLogoUrl);
  const resetLogo = (input: FieldInputProps<string, HTMLElement>): void => {
    input.onChange(defaultLogoUrl);
  };
  const isLogoDefault = (url: string): boolean => url === defaultLogoUrl;

  return (
    <Flex gap="20px" column>
      <Flex justifyContent="space-between">
        <Text>Show logo</Text>
        <Field type="checkbox" name="appearance.appearanceShowLogo.value">
          {({ input }) => <LogoSwitch input={input} />}
        </Field>
      </Flex>
      {showLogo && (
        <div>
          <Flex justifyContent="space-between" flexWrap>
            <Field name="appearance.appearanceLogoUrl.value" value={logoUrl}>
              {({ input }) => (
                <>
                  <Flex gap="4px" alignItems="center">
                    <Icon source={Image} size="small" />
                    <Flex alignItems="center">
                      <Text bold css={logoFileNameStyles}>
                        {getLogoName(input.value, isLogoDefault(input.value))}
                      </Text>
                      {!isLogoDefault(input.value) && (
                        <Tooltip
                          triggerRenderer={() => (
                            <Button
                              kind="plain"
                              size="compact"
                              icon={<Icon source={Close} size="small" />}
                              onClick={() => resetLogo(input)}
                              data-testid="button-ResetLogo"
                            />
                          )}
                          theme="invert"
                          placement="top"
                        >
                          <Text>Restore default HelpDesk logo.</Text>
                        </Tooltip>
                      )}
                    </Flex>
                  </Flex>
                  <LogoUploadField templateID={templateID} input={input} setTransactionID={setTransactionID} />
                </>
              )}
            </Field>
          </Flex>
          <Text size="sm" css={fileRecommendationStyles}>
            Supported file formats are JPG, JPEG, and PNG. Max. file size is 1MB.
            <Tooltip
              triggerRenderer={() => (
                <Icon
                  source={Info}
                  css={css`
                    display: inline;
                    vertical-align: middle;
                  `}
                  size="small"
                />
              )}
              placement="top-end"
              theme="invert"
            >
              <Text bold>File recommendations:</Text>
              <ul>
                <li>Available file formats: JPG, JPEG, PNG</li>
                <li>Background: transparent</li>
                <li>Max. file size: 1MB</li>
                <li>Max. file resolution: 1600x800px</li>
                <li>Min. file resolution: 800x400px</li>
              </ul>
            </Tooltip>
          </Text>
        </div>
      )}
    </Flex>
  );
}
